import { Schema, SchemaAST, SchemaTransformation, Predicate } from 'effect';

// effect/Schema has no built-in email validator (confirmed in
// docs/cloned-repos-as-docs/effect/packages/effect/schema-vs-zod.md). This is
// zod 4.4.3's `z.email()` default (`zod/v4/core/regexes.ts`, `email`), kept
// semantically identical so the forms that migrated off zod accept exactly
// what they did before: a dotted domain ending in a 2+ letter TLD, no leading
// dot and no `..` in the local part. Defined once here and reused so the
// pattern lives in a single place.
const EMAIL_REGEX =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+.-]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/;

// The message stays English on purpose: this is shared with the backend, where
// it ends up in API responses, logs and alerts, and where locale is per-request
// state a module-level schema cannot read. A UI that shows the message to a
// user overrides it at the edge instead — `Email.annotate({ message })`, BEFORE
// any `.check()` (annotating after one does not reach the check, and silently
// keeps this default). See `makeAuthFormSchema` in `login-logout.hook.ts`.
const Email = Schema.String.pipe(
  Schema.check(
    Schema.isPattern(EMAIL_REGEX, {
      message: 'Must be a valid email address',
      identifier: 'Email',
    }),
  ),
);

// E.164: a `+`, a nonzero leading digit, then up to 14 more digits. Mirrors the
// rule Chatwoot enforces on `Contact#phone_number`, so a lead that passes here
// cannot be rejected by the helpdesk on format. Callers must strip separators
// (spaces, parens, dashes) first — but never the leading `+`.
const E164_PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

const E164Phone = Schema.String.pipe(
  Schema.check(
    Schema.isPattern(E164_PHONE_REGEX, {
      message: 'Must be an E.164 phone number, e.g. +15551234567',
      identifier: 'E164Phone',
    }),
  ),
);

// Coercion schemas - accept native types OR strings, output native types
// Use these when values may come from JSON (native) or env vars (strings)

const BooleanOrBooleanFromString = Schema.Union([
  Schema.Boolean,
  Schema.String.pipe(
    Schema.decodeTo(
      Schema.Boolean,
      SchemaTransformation.transform({
        decode: (s) => s === 'true',
        encode: (b) => (b ? 'true' : 'false'),
      }),
    ),
  ),
]);

const NumberOrNumberFromString = Schema.Union([
  Schema.Finite,
  Schema.FiniteFromString,
]);

const StringArrayOrStringArrayFromCsv = Schema.Union([
  Schema.Array(Schema.String),
  Schema.String.pipe(
    Schema.decodeTo(
      Schema.Array(Schema.String),
      SchemaTransformation.transform<ReadonlyArray<string>, string>({
        decode: (s) => s.split(',').filter(Boolean),
        encode: (arr) => arr.join(','),
      }),
    ),
  ),
]);

// Effect Schema equivalent of Zod's `.catch(undefined)` for a literal
// union. Decodes a string to one of `literals` or `undefined` if it
// doesn't match. Use in route `validateSearch` so an unexpected value
// renders the page instead of triggering the errorComponent — see
// docs/cloned-repos-as-docs/router/docs/router/guide/search-params.md.
//
// Decode-direction only: encode maps `undefined` → `''`, which decodes
// back to `undefined` (lossy round-trip). Fine for `validateSearch`,
// which only uses the decode side. Don't reach for this if you need
// round-trip serialization.
const LiteralOrUndefined = <const L extends readonly [string, ...string[]]>(
  ...literals: L
) => {
  const allowed = new Set<string>(literals);
  return Schema.String.pipe(
    Schema.decodeTo(
      Schema.UndefinedOr(Schema.Literals([...literals])),
      SchemaTransformation.transform({
        decode: (s) => (allowed.has(s) ? (s as L[number]) : undefined),
        encode: (s) => s ?? '',
      }),
    ),
  );
};

/**
 * Extract keys from a Struct schema where the field decodes to boolean.
 * Uses AST introspection to check the output type of each field.
 */
const getBooleanEnvKeys = <T extends Schema.Struct.Fields>(
  schema: Schema.Struct<T>,
): string[] => {
  const result: string[] = [];

  for (const [key, fieldSchema] of Object.entries(schema.fields)) {
    if (isBooleanSchema(fieldSchema as Schema.Top)) {
      result.push(key);
    }
  }

  return result;
};

/**
 * Check if an AST node decodes to boolean. Handles direct booleans and
 * unions (nested unions recurse); `Schema.toType` already collapses any
 * transformation down to its decoded-side AST, so a transformation-to-boolean
 * field surfaces here as a plain `Boolean` node.
 */
const isBooleanAst = (ast: SchemaAST.AST): boolean =>
  Predicate.isTagged(ast, 'Boolean') ||
  (Predicate.isTagged(ast, 'Union') && ast.types.some(isBooleanAst));

/**
 * Check if a schema decodes to boolean by inspecting its AST.
 * Handles direct booleans, unions, transformations, and optional wrappers.
 */
const isBooleanSchema = (schema: Schema.Top): boolean =>
  isBooleanAst(Schema.toType(schema).ast);

// HttpApi clients decode responses into Effect `Schema.Class` instances.
// seroval (TanStack Router SSR dehydrate) and TanStack DB collections need
// plain objects, not class instances, so shallow-copy the own enumerable
// fields into a fresh object. The mapped return type (not `T`) is what makes
// this the one sanctioned place for the conversion: it widens the class
// instance to a plain object type so neither this spread nor any downstream
// spread of the result trips `typescript-eslint/no-misused-spread`. Route
// every Schema.Class → plain conversion through here rather than inline.
const schemaInstanceToPlain = <T extends object>(
  instance: T,
): { [K in keyof T]: T[K] } => ({ ...instance });

export const SchemaUtils = {
  Email,
  E164Phone,
  BooleanOrBooleanFromString,
  NumberOrNumberFromString,
  StringArrayOrStringArrayFromCsv,
  LiteralOrUndefined,
  getBooleanEnvKeys,
  schemaInstanceToPlain,
};
